// All of the Login component's API calls and helpers go here.
import * as firebase from "firebase/app";
import "firebase/auth";
import firebaseConfig from "../config/firebaseConfig";
import "firebase/firestore";
import "firebase/functions";
import "firebase/messaging";

// Setting up the firebase app
const firebaseApp = firebase.initializeApp(firebaseConfig);
const store = firebase.firestore();
firebase.auth().languageCode = "en";

let messaging: any = null;

if (firebase.messaging.isSupported()) {
  messaging = firebase.messaging()
  messaging.usePublicVapidKey("BE3dX7CfDy-_TNjiihMC-xodjNRnre1a8n_HHDjAfKilMXHfK2P7eQEV6OJ62EWjsADgjaPrXxNfU2pqr4n2VjY");
  // public web push certificate
  messaging.onMessage((payload: any) => console.log("Message received. ", payload));
}



// Defining a new type that can only take two possible values
type UserType = "donor" | "requester";


async function setupPushNotifs(uid: string, userType: any) {
  // requesting permissions for push notifications on successful login
  if (!messaging) return
  try {
    await messaging.requestPermission();
    const registrationToken = await messaging.getToken();
    const subscribeDeviceToTopic = firebase.functions().httpsCallable("subscribeDeviceToTopic");
    await subscribeDeviceToTopic({ registrationToken, uid, userType })//sending user's uid, userType and device registrationToken
      
    navigator.serviceWorker.addEventListener("message", (message) =>
      console.log(message)
    );
  }
  catch (err) {
    console.log("Unable to get permission to notify.", err);
  }
}



/**
* Returns the user type using user id.
* REQUIREMENTS: user id MUST exist in doc, otherwise "requester" is returned.
* @param uid user id (must exist in docs)
*/
export function getUserType(uid: string): Promise<UserType | Error> {
  return new Promise((resolve, reject) => {
    store
      .collection("donor")
      .doc(uid)
      .get()
      .then((doc) => {
        if (doc.exists) {
          resolve("donor");
        } else {
          // User not found in donors, then must be in requester
          resolve("requester");
        }
      })
      .catch((err) => reject(err));
  });
}


/**
 * Logs in user. Returns a promise that resolves to user if successful
 * or rejects with error.
 * @param email User email address
 * @param password User password
 */
export async function login(email: string, password: string): Promise<any> {
  return new Promise((resolve, reject) => {
    firebaseApp
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(async (resp: { user: any }) => {
        const uid = resp.user.uid;
        const userType = await getUserType(uid);

        const res = await setupPushNotifs(uid, userType); //for that specific user and his current device
        
        resolve(resp.user); // until login option selected do not proceed
      })
      .catch((error) => {
        console.log("Login failed: ", error.message);
        reject(error);
      });
  });
}

/**
 * Logs out the current user
 * @param None
 */
export async function logout() {
  firebaseApp
    .auth()
    .signOut()
    .then(() => {
      console.log("Successful log out");
    })
    .catch((err) => {
      console.log("Logout failed ", err);
    });
}

/**
 * Registers user by making its account and storing in database. User
 * is also signed in.
 * @param registrationData Object containing name, email and password of user
 */
export async function register(
  registrationData: {
    name: string;
    email: string;
    password: string;
    city: string;
    bloodGroup?: string;
    contact: string;
  },
  userType: UserType
): Promise<any> {
  return new Promise((resolve, reject) => {
    firebaseApp
      .auth()
      .createUserWithEmailAndPassword(
        registrationData.email,
        registrationData.password
      )
      .then((resp: { user: any }) => {
        const { password, ...data }: any = registrationData;
        data.devices = []; //array of device registration tokens for all users
        if (userType === "requester") {
          data.requests = [];
        }
        setTimeout(() => {
          store
            .collection(userType)
            .doc(resp.user.uid)
            .set({
              ...data,
              userType,
            })
            .then(() => {
              resolve(resp.user);
            })
            .catch((error) => {
              reject(error);
            });
        }, 100);
        // console.log("Registration successful");
      })
      .catch((error) => {
        reject(error);
      });
  });
}


/**
 * Returns the current logged in user. If no logged in, return false.
 * @param None
 */
export async function getLoggedInUser(): Promise<any> {
  return new Promise((resolve, reject) => {
    firebaseApp.auth().onAuthStateChanged((user) => {
      if (user) resolve(user);
      else reject(false);
    });
  });
}

/**
 * Updates the name of the Firebase.User using getLoggedInUser
 * @param name Updated name
 */
export async function updateUserName(name: string): Promise<any> {
  return getLoggedInUser().then((user) => {
    return user.updateProfile({
      displayName: name,
    });
  });
}

/**
 * Update user password by first verifying with old password.
 * @param oldPassword old password user for verification
 * @param newPassword the new password for the logged in user
 */
export async function updatePassword(
  oldPassword: string,
  newPassword: string
): Promise<any> {
  return getLoggedInUser().then((user) => {
    // verfiying old password
    const credential = firebase.auth.EmailAuthProvider.credential(
      user.email,
      oldPassword
    );
    return user.reauthenticateWithCredential(credential).then(() => {
      return user.updatePassword(newPassword);
    });
  });
}

/**
 * Deletes the current logged in user if it provides the correct password.
 * @param providedPassword Password provided by logged in user
 */
export async function deleteUser(
  providedPassword: string,
  collection: string
): Promise<any> {
  return new Promise((resolve, reject) => {
    const user: any = firebase.auth().currentUser;
    const credential = firebase.auth.EmailAuthProvider.credential(
      user?.email,
      providedPassword
    );
    user
      ?.reauthenticateWithCredential(credential)
      .then(() => {
        // First deleting user details from database
        store
          .collection(collection)
          .doc(user.uid)
          .delete()
          .then(() => {
            // Finally deleting account as well
            user
              ?.delete()
              .then(() => {
                resolve("User account deleted successfully");
              })
              .catch((err: any) => reject(err));
          })
          .catch(function (err) {
            reject(err);
          });
      })
      .catch((err: any) => reject(err));
  });
}

/**
 * Resets the password of the provided email.
 * @param email email of user
 */
export async function resetPassword(email: string): Promise<any> {
  return firebaseApp.auth().sendPasswordResetEmail(email);
}

//////////// OTP Related API //////////////

declare global {
  interface Window {
    recaptchaVerifier: any;
    confirmationResult: any;
  }
}

export async function onSignInSubmit(phoneNumber: string): Promise<any> {
  const appVerifier = window.recaptchaVerifier;

  firebase
    .auth()
    .signInWithPhoneNumber(phoneNumber, appVerifier)
    .then(function (confirmationResult) {
      // SMS sent. Prompt user to type the code from the message, then sign the
      // user in with confirmationResult.confirm(code).
      window.confirmationResult = confirmationResult;
    })
    .catch(function (error: any) {
      // Error; SMS not sent
      // ...
      return(false);
    });
}

export async function verifyOTP(code: unknown) {
  return window.confirmationResult
    .confirm(code)
    .then(() => {
      return true;
    })
    .catch((error: any) => {
      // User couldn't sign in (bad verification code?)
      return false;
    });
}

export async function attachRecaptcha(id: string) {
  window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(id, {
    size: "invisible",
    // callback: (response: any) => {
    //   // reCAPTCHA solved, allow signInWithPhoneNumber.
    //   console.log("recaptcha", response);
    // },
  });
}


export async function snoozeNotifications(snoozeDate: Date, uid: string, userType: UserType){
  await store.collection(userType).doc(uid).update({ 
    snoozedTill : snoozeDate, 
    snooze : true 
  });


  const snoozeNotificationsCF = firebase.functions().httpsCallable("snoozeNotifications");
  await snoozeNotificationsCF({ uid, userType });//sending user's uid, userType
}

export async function unsnoozeNotifications(uid: string, userType: UserType) {
  await store.collection(userType).doc(uid).update({ 
    snoozedTill: "", 
    snooze: false 
  });

  const unsnoozeNotificationsCF = firebase.functions().httpsCallable("unsnoozeNotifications");
  await unsnoozeNotificationsCF({ uid, userType });//sending user's uid, userType
}

/**
 * Checks for registered phone numbers in the database.
 * If the number exists returns True.
 * @param phoneNumber phone number of user.
 */
export async function checkPhoneNumberAccounts(phoneNumber: string): Promise<boolean | Error> {
  return new Promise((resolve, reject) => {
    store
      .collection("donor")
      .where("contact", "==", phoneNumber)
      .get()
      .then((snapshot) => {
        if (!snapshot.empty) {
          resolve(true);
        } else {
          store
            .collection("requester")
            .where("contact", "==", phoneNumber)
            .get()
            .then((snapshot) => {
              if (!snapshot.empty) {
                resolve(true);
              } else {
                resolve(false);
              }
            })
            .catch((err) => reject(err))
        }
      })
      .catch((err) => reject(err));
  });
}

export default firebaseApp;
